import { useEffect, useRef, RefObject } from 'react';

/**
 * Custom hook for managing focus in modals and dynamic content
 * Ensures proper focus management for accessibility
 */
export const useFocusManagement = (isOpen: boolean, initialFocusRef?: RefObject<HTMLElement>) => {
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Store the currently focused element
      previousFocusRef.current = document.activeElement as HTMLElement;

      // Focus the initial element or the modal container
      const elementToFocus = initialFocusRef?.current || modalRef.current;
      if (elementToFocus) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          elementToFocus.focus();
        }, 100);
      }

      // Prevent focus from leaving the modal
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Tab' && modalRef.current) {
          const focusableElements = modalRef.current.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          const firstElement = focusableElements[0] as HTMLElement;
          const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

          if (event.shiftKey) {
            // Shift + Tab
            if (document.activeElement === firstElement) {
              event.preventDefault();
              lastElement?.focus();
            }
          } else {
            // Tab
            if (document.activeElement === lastElement) {
              event.preventDefault();
              firstElement?.focus();
            }
          }
        }

        // Close modal on Escape
        if (event.key === 'Escape') {
          // This will be handled by the modal's onClose prop
          // We just need to ensure focus returns properly
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      // Return focus to the previously focused element
      if (previousFocusRef.current) {
        setTimeout(() => {
          previousFocusRef.current?.focus();
        }, 100);
      }
    }
  }, [isOpen, initialFocusRef]);

  return modalRef;
};

/**
 * Hook for managing focus trap within a container
 */
export const useFocusTrap = (containerRef: RefObject<HTMLElement>, isActive: boolean = true) => {
  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement?.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement?.focus();
          }
        }
      }
    };

    container.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
    };
  }, [containerRef, isActive]);
};

/**
 * Hook for announcing dynamic content changes to screen readers
 */
export const useAnnounceChanges = () => {
  const announceRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Create announcement element if it doesn't exist
    if (!announceRef.current) {
      const announceElement = document.createElement('div');
      announceElement.setAttribute('aria-live', 'polite');
      announceElement.setAttribute('aria-atomic', 'true');
      announceElement.style.position = 'absolute';
      announceElement.style.left = '-10000px';
      announceElement.style.width = '1px';
      announceElement.style.height = '1px';
      announceElement.style.overflow = 'hidden';
      document.body.appendChild(announceElement);
      announceRef.current = announceElement;
    }

    return () => {
      if (announceRef.current && announceRef.current.parentNode) {
        announceRef.current.parentNode.removeChild(announceRef.current);
      }
    };
  }, []);

  const announce = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    if (announceRef.current) {
      announceRef.current.setAttribute('aria-live', priority);
      announceRef.current.textContent = message;

      // Clear the message after a short delay to allow re-announcement of the same message
      setTimeout(() => {
        if (announceRef.current) {
          announceRef.current.textContent = '';
        }
      }, 1000);
    }
  };

  return announce;
};